import { useGoalMetricDayProgress } from '@griply/hooks';
import {
  countMetricOptions,
  EntityLinks,
  Goal,
  GoalProgressCount,
  ID,
  metricUnitFormatBase,
} from '@griply/types';
import {
  getCompletionPercentage,
  getGoalCompletion,
  getGoalUnitFormat,
  getTimeframeTimes,
  getWeekStartsOn,
  isGoalProgressUpdatable,
} from '@griply/utils';
import { t } from 'i18next';
import React, { useMemo, useRef } from 'react';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { TimeProgressChart } from 'shared/components/ui/time-progress-chart';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useOpenGoalMetricsForm } from 'shared/contexts/goal-metrics-form';
import { useToday } from 'shared/contexts/today';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './goal-list-card.style';

export type MetricCellProps = {
  goal: Goal;
  goals: Goal[];
  // Caller-supplied: a UI component must not fetch — without it a migrated goal
  // resolves no parent and never inherits its parent's archived state.
  links?: EntityLinks;
  isProject: boolean;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  onUpdateProgress?: (id: ID) => void;
  onCloseContextMenu: () => void;
};

export const MetricCell: React.FC<MetricCellProps> = ({
  goal,
  goals,
  links,
  isProject,
  goalProgressCounts,
  onUpdateProgress,
  onCloseContextMenu,
}) => {
  const today = useToday();
  const user = useUser();
  const locale = useI18nLocale();
  const numberFormat = useNumberFormat();
  const openMetricsForm = useOpenGoalMetricsForm();
  const metricCellRef = useRef<HTMLButtonElement>(null);

  const hasProgressMetric =
    !!goal.metric?.id && !countMetricOptions.includes(goal.metric?.id);

  const { start, end, current } = useMemo(
    () => getGoalCompletion(goal, { goalProgressCounts }),
    [goal, goalProgressCounts],
  );
  const completionPercentage = useMemo(
    () => getCompletionPercentage({ start, end, current }),
    [current, end, start],
  );

  const metricEntries = useGoalMetricDayProgress(goal);
  const metricChartRange = useMemo(
    () => getTimeframeTimes(goal, { allTasks: [], today }),
    [goal, today],
  );
  const metricUnitFormat = getGoalUnitFormat(goal.metric, locale);
  const metricCurrentValue =
    metricEntries[metricEntries.length - 1]?.value ??
    goal.metric?.startValue ??
    0;
  const formattedMetricValue = formatNumberShort(
    metricCurrentValue,
    numberFormat?.decimal,
  );

  const formattedMetricTarget = isNumber(goal.metric?.targetValue)
    ? formatNumberShort(goal.metric!.targetValue!, numberFormat?.decimal)
    : undefined;
  const metricTargetLabel = formattedMetricTarget
    ? metricUnitFormat
      ? metricUnitFormat.replace(metricUnitFormatBase, formattedMetricTarget)
      : formattedMetricTarget
    : undefined;
  const metricTooltipLabel = metricTargetLabel
    ? `${formattedMetricValue} / ${metricTargetLabel}`
    : formattedMetricValue;

  const metricUpdateEnabled =
    !!onUpdateProgress && isGoalProgressUpdatable(goal, { goals, links });

  const onProgressSelectClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openMetricsForm(goal);
  };

  const onUpdateProgressClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onUpdateProgress?.(goal.id);
    onCloseContextMenu();
  };

  return (
    <Styled.DataContainer
      $type={Styled.DataTypes.Progress}
      $isFrozen={!!goal.frozenAt}
      $isClickable={
        !goal.frozenAt && !(hasProgressMetric && !isProject) && end === Infinity
      }
      onClick={
        !goal.frozenAt && !(hasProgressMetric && !isProject) && end === Infinity
          ? onProgressSelectClick
          : undefined
      }
    >
      {hasProgressMetric && !isProject ? (
        <Styled.MetricButton
          ref={metricCellRef}
          onClick={metricUpdateEnabled ? onUpdateProgressClick : undefined}
          as={metricUpdateEnabled ? undefined : 'span'}
          $static={!metricUpdateEnabled}
        >
          <Styled.MetricChartContainer>
            <TimeProgressChart
              data={metricEntries}
              startDate={goal.startDate}
              endDate={goal.deadline}
              chartStartDate={metricChartRange.startDate}
              chartEndDate={metricChartRange.endDate}
              startValue={goal.metric?.startValue ?? undefined}
              endValue={goal.metric?.targetValue ?? undefined}
              weekStartsOn={getWeekStartsOn(user)}
              isComplete={completionPercentage >= 100}
              hideYAxis
              hideXAxis
              hideDot
              hasSmallHeight
              height={16}
              numberFormat={numberFormat}
            />
          </Styled.MetricChartContainer>
          <Styled.MetricValue>{`${completionPercentage}%`}</Styled.MetricValue>
          <Tooltip
            referenceElement={metricCellRef}
            label={metricTooltipLabel}
            delay={0}
          />
        </Styled.MetricButton>
      ) : end !== Infinity ? (
        <Styled.MetricButton as="span" $static>
          <Styled.MetricChartContainer>
            <CircleProgress
              percentage={completionPercentage}
              isPrimary={!goal.frozenAt}
              isSecondary={!!goal.frozenAt}
              successOnComplete
              hidePercentage
            />
          </Styled.MetricChartContainer>
          <Styled.MetricValue>{`${completionPercentage}%`}</Styled.MetricValue>
        </Styled.MetricButton>
      ) : (
        <Styled.SelectPlaceholder>
          {t('general.labels.select')}
        </Styled.SelectPlaceholder>
      )}
    </Styled.DataContainer>
  );
};
